/**
 * Utilitário para tratar erros de rede do Firestore
 * Especialmente erros QUIC e outros erros de conectividade temporários
 */

export interface FirestoreError {
  code: string;
  message: string;
  name?: string;
}

/**
 * Verifica se um erro é um erro de rede temporário que pode ser ignorado
 */
export function isNetworkError(error: any): boolean {
  if (!error) return false;

  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = error.code?.toLowerCase() || '';
  const errorName = error.name?.toLowerCase() || '';

  // Erros de rede temporários
  const networkErrors = [
    'err_quic_protocol_error',
    'quic_protocol_error',
    'network_error',
    'network-request-failed',
    'unavailable',
    'deadline-exceeded',
    'internal',
    'failed-precondition',
    'aborted',
    'cancelled',
  ];

  // Verificar código, nome e mensagem
  return (
    networkErrors.some(err => 
      errorCode.includes(err) || 
      errorName.includes(err) || 
      errorMessage.includes(err)
    ) ||
    // Verificar se é um erro de QUIC específico
    errorMessage.includes('quic') ||
    errorMessage.includes('protocol error') ||
    // Verificar se é um erro de timeout
    errorMessage.includes('timeout') ||
    errorMessage.includes('timed out')
  );
}

/**
 * Verifica se um erro é um erro crítico que requer ação
 */
export function isCriticalError(error: any): boolean {
  if (!error) return false;

  const errorCode = error.code?.toLowerCase() || '';
  
  const criticalErrors = [
    'permission-denied',
    'unauthenticated',
    'not-found',
    'invalid-argument',
    'failed-precondition',
  ];

  return criticalErrors.some(err => errorCode.includes(err));
}

/**
 * Trata erros do Firestore de forma inteligente
 * Suprime logs de erros de rede temporários
 * Loga erros críticos
 */
export function handleFirestoreError(
  error: any,
  context: string = 'Firestore',
  options: {
    suppressNetworkErrors?: boolean;
    retryCallback?: () => void;
    maxRetries?: number;
  } = {}
): void {
  const {
    suppressNetworkErrors = true,
    retryCallback,
    maxRetries = 3,
  } = options;

  if (!error) return;

  // Erros de rede temporários - geralmente podem ser ignorados
  // pois o Firestore tenta reconectar automaticamente
  if (isNetworkError(error) && suppressNetworkErrors) {
    // Log apenas em modo desenvolvimento e com throttle
    if (process.env.NODE_ENV === 'development') {
      const lastLogKey = `firestore_error_log_${context}`;
      const lastLogTime = localStorage.getItem(lastLogKey);
      const now = Date.now();
      
      // Log apenas uma vez a cada 5 minutos para não encher o console
      if (!lastLogTime || now - parseInt(lastLogTime) > 5 * 60 * 1000) {
        console.warn(
          `⚠️ [${context}] Erro de rede temporário (Firestore tentando reconectar):`,
          error.message || error.code || 'Erro desconhecido'
        );
        localStorage.setItem(lastLogKey, now.toString());
      }
    }
    return;
  }

  // Erros críticos - sempre logar
  if (isCriticalError(error)) {
    console.error(`❌ [${context}] Erro crítico do Firestore:`, {
      code: error.code,
      message: error.message,
      name: error.name,
      error,
    });
    return;
  }

  // Outros erros - logar normalmente
  console.error(`❌ [${context}] Erro do Firestore:`, {
    code: error.code,
    message: error.message,
    name: error.name,
    error,
  });
}

/**
 * Wrapper para listeners do Firestore com tratamento de erros
 */
export function createFirestoreListener<T>(
  setupListener: (onNext: (data: T) => void, onError: (error: any) => void) => () => void,
  context: string = 'Firestore',
  options: {
    suppressNetworkErrors?: boolean;
    onError?: (error: any) => void;
  } = {}
): () => void {
  let unsubscribe: (() => void) | null = null;
  let retryCount = 0;
  const maxRetries = 3;

  const setup = () => {
    try {
      unsubscribe = setupListener(
        (data: T) => {
          // Reset retry count on success
          retryCount = 0;
        },
        (error: any) => {
          handleFirestoreError(error, context, {
            suppressNetworkErrors: options.suppressNetworkErrors ?? true,
          });

          // Call custom error handler if provided
          if (options.onError) {
            options.onError(error);
          }

          // Retry logic for network errors
          if (isNetworkError(error) && retryCount < maxRetries) {
            retryCount++;
            console.log(
              `🔄 [${context}] Tentando reconectar... (tentativa ${retryCount}/${maxRetries})`
            );
            
            // Wait before retrying (exponential backoff)
            setTimeout(() => {
              if (unsubscribe) {
                unsubscribe();
              }
              setup();
            }, Math.pow(2, retryCount) * 1000); // 2s, 4s, 8s
          }
        }
      );
    } catch (error) {
      handleFirestoreError(error, context, {
        suppressNetworkErrors: options.suppressNetworkErrors ?? true,
      });
    }
  };

  setup();

  return () => {
    if (unsubscribe) {
      unsubscribe();
    }
  };
}



















